import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { useLiveTime, formatNowClock } from '../hooks/useLiveTime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('az-AZ', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch { return null; }
};

const daysLeft = (iso) => {
  if (!iso) return null;
  const diff = Math.ceil((new Date(iso) - new Date()) / (1000 * 60 * 60 * 24));
  return diff;
};

const toIsoDateInput = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch { return ''; }
};

export const Dashboard = () => {
  const { user, logout } = useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [subModalFor, setSubModalFor] = useState(null);
  const [deleteConfirmFor, setDeleteConfirmFor] = useState(null);
  const [passwordModalFor, setPasswordModalFor] = useState(null);
  const [contractsModalFor, setContractsModalFor] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [welcomeOpen, setWelcomeOpen] = useState(searchParams.get('welcome') === '1');
  const navigate = useNavigate();
  const nowMs = useLiveTime(1000);
  const clock = formatNowClock(nowMs);

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const fetchRestaurants = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/restaurants`, {
        withCredentials: true,
      });
      setRestaurants(data);
    } catch (error) {
      console.error('Restoranlar yüklənmədi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleToggleActive = async (restaurantId, currentlyActive, e) => {
    e.stopPropagation();
    if (!isSuperAdmin) return;
    setTogglingId(restaurantId);
    try {
      const { data } = await axios.patch(
        `${BACKEND_URL}/api/restaurants/${restaurantId}/toggle-active`,
        { is_active: !currentlyActive },
        { withCredentials: true }
      );
      setRestaurants((prev) =>
        prev.map((r) => (r.id === restaurantId ? { ...r, is_active: data.is_active } : r))
      );
    } catch (err) {
      alert('Status dəyişdirilə bilmədi: ' + (err.response?.data?.detail || err.message));
    } finally {
      setTogglingId(null);
    }
  };

  const handleSetSubscription = async (restaurantId, start, end) => {
    try {
      const { data } = await axios.patch(
        `${BACKEND_URL}/api/restaurants/${restaurantId}/subscription`,
        {
          subscription_start: start ? new Date(start).toISOString() : null,
          subscription_end: end ? new Date(end).toISOString() : null,
        },
        { withCredentials: true }
      );
      setRestaurants((prev) =>
        prev.map((r) => (r.id === restaurantId ? { ...r, ...data } : r))
      );
      setSubModalFor(null);
    } catch (err) {
      alert('Müqavilə tarixi təyin oluna bilmədi: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (restaurantId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/restaurants/${restaurantId}`, {
        withCredentials: true,
      });
      setRestaurants((prev) => prev.filter((r) => r.id !== restaurantId));
      setDeleteConfirmFor(null);
    } catch (err) {
      alert('Silmə xətası: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSlugUpdate = async (restaurantId, newSlug) => {
    const { data } = await axios.patch(
      `${BACKEND_URL}/api/restaurants/${restaurantId}/slug`,
      { slug: newSlug },
      { withCredentials: true }
    );
    setRestaurants((prev) =>
      prev.map((r) => (r.id === restaurantId ? { ...r, slug: data.slug, qr_code: data.qr_code } : r))
    );
    return data;
  };

  const closeWelcome = () => {
    setWelcomeOpen(false);
    searchParams.delete('welcome');
    setSearchParams(searchParams, { replace: true });
  };

  const filtered = restaurants.filter((r) => {
    if (filter === 'active') return r.is_active;
    if (filter === 'inactive') return !r.is_active;
    if (filter === 'restaurant') return (r.business_type || 'restaurant') === 'restaurant';
    if (filter === 'cafe') return r.business_type === 'cafe';
    return true;
  });

  const stats = {
    total: restaurants.length,
    active: restaurants.filter((r) => r.is_active).length,
    inactive: restaurants.filter((r) => !r.is_active).length,
    restaurant: restaurants.filter((r) => (r.business_type || 'restaurant') === 'restaurant').length,
    cafe: restaurants.filter((r) => r.business_type === 'cafe').length,
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <nav className="bg-white shadow-sm border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-stone-900" data-testid="dashboard-title">
                {isSuperAdmin ? 'SmartMenyu Admin Paneli' : 'Restoran İdarəetmə'}
              </h1>
            </div>
            <div className="flex items-center space-x-3">
              <div
                className="flex items-center gap-2 px-3 py-1.5 bg-stone-50 border border-stone-200 rounded-lg"
                data-testid="dashboard-live-clock"
              >
                <span className="text-lg">🕐</span>
                <div className="leading-tight">
                  <div className="font-mono text-sm font-bold text-stone-900" data-testid="dashboard-clock-time">{clock.time}</div>
                  <div className="text-[10px] text-stone-500" data-testid="dashboard-clock-date">{clock.date}</div>
                </div>
              </div>
              <span className="text-sm text-stone-600 hidden sm:inline" data-testid="dashboard-user-info">
                {user?.full_name}
                <span className="ml-2 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full font-semibold uppercase">
                  {user?.role === 'super_admin' ? 'Admin' : 'Sahib'}
                </span>
              </span>
              {isSuperAdmin && (
                <>
                  <button
                    onClick={() => navigate('/admin/mail')}
                    className="px-3 sm:px-4 py-2 text-sm bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition font-medium whitespace-nowrap"
                    data-testid="mail-panel-button"
                  >
                    📬 <span className="hidden sm:inline">Mail Paneli</span>
                  </button>
                  <button
                    onClick={() => navigate('/admin/landing')}
                    className="px-3 sm:px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition font-medium whitespace-nowrap"
                    data-testid="landing-admin-button"
                  >
                    🎨 <span className="hidden sm:inline">Saytı redaktə et</span>
                  </button>
                </>
              )}
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg transition font-medium"
                data-testid="dashboard-logout-button"
              >
                Çıxış
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* WELCOME BANNER after registration */}
        {welcomeOpen && (
          <div
            className="mb-6 bg-gradient-to-r from-emerald-50 to-emerald-100 border-l-4 border-emerald-500 rounded-xl p-5 flex items-start gap-4 shadow-sm"
            data-testid="welcome-banner"
          >
            <div className="text-3xl">🎉</div>
            <div className="flex-1">
              <h3 className="font-bold text-emerald-900 text-lg mb-1">
                Qeydiyyatınız uğurla başa yekunlaşmışdır!
              </h3>
              <p className="text-sm text-emerald-800">
                Administrator ilə müqavilə imzalandıqdan sonra restoranınız aktiv ediləcək və QR menyunuz işə düşəcək.
                Hələlik aşağıda öz restoranınızın passiv kartını görəcəksiniz.
              </p>
            </div>
            <button
              onClick={closeWelcome}
              className="text-emerald-700 hover:text-emerald-900 text-2xl leading-none"
              data-testid="welcome-banner-close"
            >×</button>
          </div>
        )}

        {/* Stats cards */}
        {isSuperAdmin && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
            <StatCard label="Cəmi" value={stats.total} color="bg-stone-100 text-stone-800" testid="stat-total" />
            <StatCard label="Aktiv" value={stats.active} color="bg-emerald-100 text-emerald-700" testid="stat-active" />
            <StatCard label="Passiv" value={stats.inactive} color="bg-red-100 text-red-700" testid="stat-inactive" />
            <StatCard label="🍽️ Restaurant" value={stats.restaurant} color="bg-amber-100 text-amber-800" testid="stat-restaurant" />
            <StatCard label="☕ Cafe" value={stats.cafe} color="bg-blue-100 text-blue-800" testid="stat-cafe" />
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
            <h2 className="text-2xl font-bold text-stone-900">
              {isSuperAdmin ? 'Qeydiyyatdan keçən restoran/cafe' : 'Restoranlarım'}
            </h2>
            <div className="flex items-center gap-2">
              {isSuperAdmin && (
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="px-3 py-2 text-sm border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  data-testid="restaurant-filter"
                >
                  <option value="all">Hamısı</option>
                  <option value="active">Aktiv</option>
                  <option value="inactive">Passiv</option>
                  <option value="restaurant">Restaurant</option>
                  <option value="cafe">Cafe</option>
                </select>
              )}
              {(user?.role === 'super_admin' || user?.role === 'restaurant_owner') && (
                <button
                  onClick={() => navigate('/restaurant/create')}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition font-medium text-sm"
                  data-testid="create-restaurant-button"
                >
                  + Yeni
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-stone-500 text-lg">
                {restaurants.length === 0
                  ? 'Hələ qeydiyyatdan keçən restoran yoxdur'
                  : 'Filtrə uyğun nəticə tapılmadı'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((restaurant) => (
                <RestaurantCard
                  key={restaurant.id}
                  restaurant={restaurant}
                  isSuperAdmin={isSuperAdmin}
                  toggling={togglingId === restaurant.id}
                  onToggleActive={handleToggleActive}
                  onOpenSubscription={() => setSubModalFor(restaurant)}
                  onRequestDelete={() => setDeleteConfirmFor(restaurant)}
                  onOpenPassword={() => setPasswordModalFor(restaurant)}
                  onOpenContracts={() => setContractsModalFor(restaurant)}
                  onOpenAnalytics={() => navigate(`/restaurant/${restaurant.id}/analytics`)}
                  onOpen={() => navigate(`/restaurant/${restaurant.id}`)}
                  onManageMenu={() => navigate(`/menu/manage/${restaurant.id}`)}
                  onSlugUpdate={handleSlugUpdate}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* SUBSCRIPTION DATE MODAL */}
      {subModalFor && (
        <SubscriptionModal
          restaurant={subModalFor}
          onClose={() => setSubModalFor(null)}
          onSave={handleSetSubscription}
        />
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {deleteConfirmFor && (
        <DeleteConfirmModal
          restaurant={deleteConfirmFor}
          onCancel={() => setDeleteConfirmFor(null)}
          onConfirm={() => handleDelete(deleteConfirmFor.id)}
        />
      )}

      {/* PASSWORD RESET MODAL (super_admin) */}
      {passwordModalFor && (
        <PasswordResetModal
          restaurant={passwordModalFor}
          onClose={() => setPasswordModalFor(null)}
        />
      )}

      {/* CONTRACT / DOCUMENT UPLOAD MODAL */}
      {contractsModalFor && (
        <ContractsModal
          restaurant={contractsModalFor}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setContractsModalFor(null)}
        />
      )}
    </div>
  );
};

const StatCard = ({ label, value, color, testid }) => (
  <div className={`rounded-xl p-3 ${color}`} data-testid={testid}>
    <div className="text-2xl font-bold">{value}</div>
    <div className="text-xs font-semibold opacity-80 mt-0.5">{label}</div>
  </div>
);

const RestaurantCard = ({
  restaurant,
  isSuperAdmin,
  toggling,
  onToggleActive,
  onOpenSubscription,
  onRequestDelete,
  onOpenPassword,
  onOpenContracts,
  onOpenAnalytics,
  onOpen,
  onManageMenu,
  onSlugUpdate,
}) => {
  const bizType = restaurant.business_type || 'restaurant';
  const isActive = !!restaurant.is_active;
  const subEnd = restaurant.subscription_end;
  const dLeft = daysLeft(subEnd);

  return (
    <div
      className={`relative border-2 rounded-xl p-5 hover:shadow-lg transition bg-white ${
        isActive ? 'border-stone-200' : 'border-red-200 bg-red-50/30'
      }`}
      data-testid={`restaurant-card-${restaurant.id}`}
    >
      {/* Top-right corner: active/passive toggle */}
      <div className="absolute top-3 right-3 flex items-center gap-2">
        {isSuperAdmin ? (
          <button
            onClick={(e) => onToggleActive(restaurant.id, isActive, e)}
            disabled={toggling}
            className={`relative inline-flex h-7 w-14 items-center rounded-full transition shadow-inner ${
              isActive ? 'bg-emerald-500' : 'bg-stone-300'
            } ${toggling ? 'opacity-50' : ''}`}
            title={isActive ? 'Aktiv — passiv et' : 'Passiv — aktiv et'}
            data-testid={`toggle-active-${restaurant.id}`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition ${
                isActive ? 'translate-x-8' : 'translate-x-1'
              }`}
            />
            <span className={`absolute text-[9px] font-bold ${isActive ? 'left-1.5 text-white' : 'right-1.5 text-stone-600'}`}>
              {isActive ? 'ON' : 'OFF'}
            </span>
          </button>
        ) : (
          <span
            className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
              isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
            }`}
            data-testid={`status-${restaurant.id}`}
          >
            {isActive ? '● Aktiv' : '● Passiv'}
          </span>
        )}
      </div>

      {/* Business type + delete row */}
      <div className="mb-3 flex items-center justify-between pr-16">
        <span
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${
            bizType === 'cafe'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-amber-100 text-amber-800'
          }`}
          data-testid={`business-type-badge-${restaurant.id}`}
        >
          {bizType === 'cafe' ? '☕ Cafe' : '🍽️ Restaurant'}
        </span>
      </div>

      <div onClick={onOpen} className="cursor-pointer">
        {restaurant.logo_url ? (
          <img
            src={restaurant.logo_url}
            alt={restaurant.name}
            className={`w-full h-28 object-cover rounded-lg mb-3 ${!isActive ? 'grayscale opacity-60' : ''}`}
          />
        ) : (
          <div className={`w-full h-28 rounded-lg mb-3 bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center text-3xl ${!isActive ? 'grayscale opacity-60' : ''}`}>
            {bizType === 'cafe' ? '☕' : '🍽️'}
          </div>
        )}

        <h3 className="text-lg font-bold text-stone-900 mb-1 truncate">
          {restaurant.name}
        </h3>

        {isSuperAdmin && restaurant.owner_full_name && (
          <div className="text-xs text-stone-500 mb-2 space-y-0.5">
            <p className="truncate">👤 {restaurant.owner_full_name}</p>
            {restaurant.owner_email && <p className="truncate">📧 {restaurant.owner_email}</p>}
            {restaurant.owner_phone && <p className="truncate">📞 {restaurant.owner_phone}</p>}
          </div>
        )}
      </div>

      {/* Slug (pretty URL) */}
      {restaurant.slug !== undefined && (
        <SlugEditor restaurant={restaurant} onUpdate={onSlugUpdate} />
      )}

      {/* Subscription info */}
      {subEnd && (
        <div
          className={`mt-2 px-2.5 py-1.5 rounded-lg text-xs font-medium border ${
            dLeft != null && dLeft > 7
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : dLeft != null && dLeft > 0
              ? 'bg-amber-50 border-amber-200 text-amber-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
          data-testid={`subscription-info-${restaurant.id}`}
        >
          📅 {formatDate(restaurant.subscription_start)} → {formatDate(subEnd)}
          {dLeft != null && (
            <span className="ml-1 font-bold">
              {dLeft > 0 ? `(${dLeft} gün qalıb)` : `(${Math.abs(dLeft)} gün öncə bitib)`}
            </span>
          )}
        </div>
      )}

      {!isActive && !subEnd && (
        <div className="mt-2 px-2.5 py-1.5 bg-red-100 border border-red-200 rounded-lg text-xs text-red-700 font-medium">
          ⚠️ QR menyu işləmir — müqavilə imzalanmayıb
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-2">
        <button
          onClick={onManageMenu}
          className="px-3 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs rounded-lg transition font-semibold"
          data-testid={`manage-menu-button-${restaurant.id}`}
        >
          Menyu
        </button>
        <a
          href={restaurant.slug ? `/${restaurant.slug}` : `/qr-menu/${restaurant.id}`}
          target="_blank"
          rel="noreferrer"
          className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-lg transition font-semibold text-center"
          data-testid={`view-qr-${restaurant.id}`}
        >
          QR menyu
        </a>
        <a
          href={`/kitchen/${restaurant.id}`}
          target="_blank"
          rel="noreferrer"
          className="px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white text-xs rounded-lg transition font-semibold text-center"
          data-testid={`kitchen-${restaurant.id}`}
        >
          🍳 Mətbəx
        </a>
        <a
          href={`/waiter/${restaurant.id}`}
          target="_blank"
          rel="noreferrer"
          className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded-lg transition font-semibold text-center"
          data-testid={`waiter-${restaurant.id}`}
        >
          🛎️ Ofisiant
        </a>
        <button
          onClick={onOpenAnalytics}
          className="px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white text-xs rounded-lg transition font-semibold flex items-center justify-center gap-1"
          data-testid={`analytics-btn-${restaurant.id}`}
        >
          📊 Analitika
        </button>
        <button
          onClick={onOpenContracts}
          className="px-3 py-2 bg-slate-700 hover:bg-slate-800 text-white text-xs rounded-lg transition font-semibold flex items-center justify-center gap-1"
          data-testid={`contracts-btn-${restaurant.id}`}
        >
          📄 Sənədlər
        </button>
        {isSuperAdmin && (
          <>
            <button
              onClick={onOpenSubscription}
              className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs rounded-lg transition font-semibold flex items-center justify-center gap-1"
              data-testid={`subscription-btn-${restaurant.id}`}
            >
              📅 Müqavilə
            </button>
            <button
              onClick={onOpenPassword}
              className="px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs rounded-lg transition font-semibold flex items-center justify-center gap-1"
              data-testid={`password-btn-${restaurant.id}`}
            >
              🔑 Şifrə təyin et
            </button>
            <button
              onClick={onRequestDelete}
              className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-xs rounded-lg transition font-semibold flex items-center justify-center gap-1 col-span-2"
              data-testid={`delete-btn-${restaurant.id}`}
            >
              🗑️ Sil
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const SubscriptionModal = ({ restaurant, onClose, onSave }) => {
  const [start, setStart] = useState(toIsoDateInput(restaurant.subscription_start));
  const [end, setEnd] = useState(toIsoDateInput(restaurant.subscription_end));
  const [saving, setSaving] = useState(false);

  const setPreset = (months) => {
    const s = new Date();
    const e = new Date();
    e.setMonth(e.getMonth() + months);
    setStart(s.toISOString().slice(0, 10));
    setEnd(e.toISOString().slice(0, 10));
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(restaurant.id, start || null, end || null);
    setSaving(false);
  };

  const handleClear = async () => {
    setSaving(true);
    await onSave(restaurant.id, null, null);
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="subscription-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="text-xl font-bold text-stone-900">📅 Müqavilə müddəti</h3>
            <p className="text-sm text-stone-500 mt-0.5">{restaurant.name}</p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600 text-2xl leading-none">×</button>
        </div>

        <div className="space-y-4">
          {/* Quick presets */}
          <div>
            <label className="block text-xs font-bold text-stone-600 uppercase mb-2">Sürətli seçim</label>
            <div className="grid grid-cols-4 gap-2">
              {[1, 3, 6, 12].map((m) => (
                <button
                  key={m}
                  onClick={() => setPreset(m)}
                  className="px-2 py-2 text-xs font-semibold border border-stone-300 rounded-lg hover:border-emerald-500 hover:bg-emerald-50 transition"
                  data-testid={`preset-${m}m`}
                >
                  {m} ay
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-stone-700 mb-1.5">
              Başlama tarixi
            </label>
            <input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="w-full px-4 py-2.5 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500"
              data-testid="subscription-start-input"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-stone-700 mb-1.5">
              Bitmə tarixi
            </label>
            <input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              min={start || undefined}
              className="w-full px-4 py-2.5 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500"
              data-testid="subscription-end-input"
            />
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800">
            ℹ️ Tarix aralığı keçərlidirsə restoran avtomatik <strong>aktiv</strong> olur.
            Bitmə tarixi keçəndən sonra sistem onu avtomatik <strong>passiv</strong> edəcək.
          </div>
        </div>

        <div className="mt-6 flex gap-2">
          <button
            onClick={handleClear}
            disabled={saving}
            className="flex-1 px-4 py-2.5 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg font-semibold text-sm disabled:opacity-50"
            data-testid="subscription-clear-button"
          >
            Təmizlə
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !start || !end}
            className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold text-sm disabled:opacity-50"
            data-testid="subscription-save-button"
          >
            {saving ? 'Yadda saxlanır...' : 'Yadda saxla'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DeleteConfirmModal = ({ restaurant, onCancel, onConfirm }) => {
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const isMatch = confirmText.trim().toLowerCase() === restaurant.name.trim().toLowerCase();

  const handleDelete = async () => {
    setDeleting(true);
    await onConfirm();
    setDeleting(false);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="delete-confirm-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center text-2xl">⚠️</div>
            <div>
              <h3 className="text-xl font-bold text-stone-900">Restoranı sil</h3>
              <p className="text-sm text-stone-500 mt-0.5">Bu əməliyyat geri qaytarıla bilməz</p>
            </div>
          </div>
          <button onClick={onCancel} className="text-stone-400 hover:text-stone-600 text-2xl leading-none">×</button>
        </div>

        <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-3 text-sm text-red-800 mb-4">
          <p className="font-semibold mb-1">&quot;{restaurant.name}&quot; restoranı və ona aid olan bütün məlumatlar silinəcək:</p>
          <ul className="list-disc list-inside text-xs space-y-0.5 mt-2">
            <li>Bütün menyu kateqoriyaları</li>
            <li>Bütün məhsullar və şəkillər</li>
            <li>QR kod (yenidən yaranmayacaq)</li>
            <li>Sahibin istifadəçi hesabı (başqa restoranı yoxdursa)</li>
          </ul>
        </div>

        <div>
          <label className="block text-sm font-semibold text-stone-700 mb-2">
            Təsdiq üçün restoran adını yazın: <span className="text-red-600 font-mono">{restaurant.name}</span>
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={restaurant.name}
            className="w-full px-4 py-2.5 border border-stone-300 rounded-xl focus:ring-2 focus:ring-red-500"
            data-testid="delete-confirm-input"
          />
        </div>

        <div className="mt-6 flex gap-2">
          <button
            onClick={onCancel}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg font-semibold text-sm"
            data-testid="delete-cancel-button"
          >
            Ləğv et
          </button>
          <button
            onClick={handleDelete}
            disabled={!isMatch || deleting}
            className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="delete-confirm-button"
          >
            {deleting ? 'Silinir...' : 'Tamamilə sil'}
          </button>
        </div>
      </div>
    </div>
  );
};


const PasswordResetModal = ({ restaurant, onClose }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const ownerId = restaurant.owner_id;
  const ownerName = restaurant.owner_full_name || '-';
  const ownerEmail = restaurant.owner_email || '-';

  const generateStrong = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$';
    let out = '';
    for (let i = 0; i < 12; i++) out += chars.charAt(Math.floor(Math.random() * chars.length));
    setPassword(out);
    setConfirm(out);
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    if (password.length < 6) return setError('Şifrə ən azı 6 simvol olmalıdır');
    if (password !== confirm) return setError('Şifrələr uyğun gəlmir');
    setSaving(true);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/users/${ownerId}/set-password`,
        { new_password: password },
        { withCredentials: true }
      );
      setSuccess(`Şifrə yeniləndi. Yeni şifrəni sahibə yazışma ilə paylaşın.`);
    } catch (err) {
      setError('Xəta: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="password-reset-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-stone-900">🔑 Şifrəni təyin et</h3>
            <p className="text-sm text-stone-500 mt-0.5">{restaurant.name}</p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600 text-2xl leading-none">×</button>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 mb-4">
          <p className="font-semibold mb-1">⚠️ Təhlükəsizlik qaydası</p>
          <p>Şifrələr birtərəfli hash-lənmiş şəkildə saxlanılır — hətta admin belə mövcud şifrəni görə bilməz. Burada yalnız yeni şifrə <strong>təyin</strong> etmək olar.</p>
        </div>

        <div className="text-sm space-y-1 mb-4 bg-stone-50 rounded-lg p-3 border border-stone-200">
          <div><span className="text-stone-500">Sahib:</span> <span className="font-semibold">{ownerName}</span></div>
          <div><span className="text-stone-500">E-poçt:</span> <span className="font-mono text-xs">{ownerEmail}</span></div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-semibold text-stone-700 mb-1.5">Yeni şifrə</label>
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Ən azı 6 simvol"
              className="w-full px-4 py-2.5 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 font-mono"
              data-testid="new-password-input"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-stone-700 mb-1.5">Təkrar şifrə</label>
            <input
              type="text"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full px-4 py-2.5 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 font-mono"
              data-testid="confirm-password-input"
            />
          </div>
          <button
            onClick={generateStrong}
            className="text-xs px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-semibold"
            data-testid="generate-password-btn"
          >
            🎲 Güclü şifrə yarat
          </button>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-2.5" data-testid="password-error">{error}</div>}
          {success && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-lg p-3" data-testid="password-success">
              <p className="font-semibold mb-1">✓ {success}</p>
              <div className="font-mono text-xs bg-white px-2 py-1 rounded mt-1 select-all">{password}</div>
            </div>
          )}
        </div>

        <div className="mt-6 flex gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-4 py-2.5 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg font-semibold text-sm"
            data-testid="password-cancel-btn"
          >
            Bağla
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !password}
            className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold text-sm disabled:opacity-50"
            data-testid="password-save-btn"
          >
            {saving ? 'Yenilənir...' : 'Şifrəni təyin et'}
          </button>
        </div>
      </div>
    </div>
  );
};

const CONTRACT_TYPES = [
  { value: 'contract', label: '📄 Müqavilə' },
  { value: 'invoice', label: '🧾 Faktura' },
  { value: 'act', label: '📋 Akt' },
  { value: 'other', label: '📎 Digər' },
];

const ContractsModal = ({ restaurant, isSuperAdmin, onClose }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState('contract');
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/restaurants/${restaurant.id}/documents`,
        { withCredentials: true }
      );
      setDocuments(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setError('');
    if (!file) return setError('Fayl seçin');
    if (!title.trim()) return setError('Ad qeyd edin');
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title.trim());
    fd.append('doc_type', docType);
    try {
      await axios.post(
        `${BACKEND_URL}/api/restaurants/${restaurant.id}/documents`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setFile(null);
      setTitle('');
      setDocType('contract');
      await fetchDocs();
    } catch (err) {
      setError('Yükləmə xətası: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Sənədi silməyə əminsiniz?')) return;
    setDeletingId(docId);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/restaurants/${restaurant.id}/documents/${docId}`,
        { withCredentials: true }
      );
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch (err) {
      alert('Silmə xətası: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingId(null);
    }
  };

  const downloadUrl = (doc) =>
    `${BACKEND_URL}/api/restaurants/${restaurant.id}/documents/${doc.id}/download`;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="contracts-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-stone-900">📄 Sənədlər</h3>
            <p className="text-sm text-stone-500 mt-0.5">{restaurant.name}</p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600 text-2xl leading-none">×</button>
        </div>

        {isSuperAdmin && (
          <form onSubmit={handleUpload} className="bg-stone-50 border border-stone-200 rounded-xl p-4 mb-5" data-testid="contract-upload-form">
            <p className="text-xs font-bold text-stone-600 uppercase mb-2">Yeni sənəd yüklə</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Sənəd adı (məs: İl 2026 müqaviləsi)"
                className="px-3 py-2 border border-stone-300 rounded-lg text-sm"
                data-testid="contract-title-input"
              />
              <select
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
                className="px-3 py-2 border border-stone-300 rounded-lg text-sm"
                data-testid="contract-type-select"
              >
                {CONTRACT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={(e) => setFile(e.target.files[0])}
              className="w-full text-sm mb-2"
              data-testid="contract-file-input"
            />
            {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg p-2 mb-2" data-testid="contract-upload-error">{error}</div>}
            <button
              type="submit"
              disabled={uploading}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold text-sm disabled:opacity-50"
              data-testid="contract-upload-btn"
            >
              {uploading ? 'Yüklənir...' : '⬆️ Yüklə'}
            </button>
            <p className="text-[11px] text-stone-500 mt-2">PDF, DOC/DOCX, JPG, PNG • Maks: 20MB. Restoran sahibi yalnız görə və endirə biləcək.</p>
          </form>
        )}

        <div>
          <p className="text-xs font-bold text-stone-600 uppercase mb-2">Mövcud sənədlər ({documents.length})</p>
          {loading ? (
            <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto" /></div>
          ) : documents.length === 0 ? (
            <div className="text-center py-8 bg-stone-50 rounded-xl border border-dashed border-stone-300" data-testid="contracts-empty">
              <p className="text-sm text-stone-500">Hələ sənəd yüklənməyib</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="contracts-list">
              {documents.map((d) => {
                const typeLbl = CONTRACT_TYPES.find((t) => t.value === d.doc_type)?.label || d.doc_type;
                const size = d.size_bytes ? (d.size_bytes / 1024 / 1024).toFixed(2) : '0';
                return (
                  <div key={d.id} className="flex items-center gap-3 bg-white border border-stone-200 rounded-xl p-3 hover:bg-stone-50" data-testid={`contract-${d.id}`}>
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center text-lg">
                      {typeLbl.slice(0, 2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-stone-900 truncate">{d.title}</p>
                      <p className="text-xs text-stone-500 truncate">{d.original_filename} • {size} MB • {formatDate(d.created_at)}</p>
                    </div>
                    <a
                      href={downloadUrl(d)}
                      target="_blank"
                      rel="noreferrer"
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-lg font-semibold"
                      data-testid={`contract-download-${d.id}`}
                    >
                      ⬇️
                    </a>
                    {isSuperAdmin && (
                      <button
                        onClick={() => handleDelete(d.id)}
                        disabled={deletingId === d.id}
                        className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs rounded-lg font-semibold disabled:opacity-50"
                        data-testid={`contract-delete-${d.id}`}
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ============================================
// SLUG EDITOR — inline UI to view / customize a restaurant's pretty URL
// ============================================
const SlugEditor = ({ restaurant, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(restaurant.slug || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://smartmenyu.az';
  const displayHost = origin.replace(/^https?:\/\//, '');

  const fullUrl = `${origin}/${restaurant.slug || ''}`;

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
    } catch { /* ignore */ }
  };

  const save = async () => {
    setError('');
    const clean = (value || '').trim().toLowerCase();
    if (!clean) {
      setError('Slug boş ola bilməz');
      return;
    }
    setSaving(true);
    try {
      await onUpdate(restaurant.id, clean);
      setEditing(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Slug yenilənmədi');
    } finally {
      setSaving(false);
    }
  };

  if (!restaurant.slug && !editing) {
    return (
      <div className="mt-2 px-2.5 py-2 bg-stone-50 border border-stone-200 rounded-lg text-xs text-stone-600">
        <button
          onClick={() => setEditing(true)}
          className="font-semibold text-emerald-700 hover:underline"
          data-testid={`slug-create-${restaurant.id}`}
        >
          + Xüsusi URL yarat
        </button>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="mt-2 px-2.5 py-2 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid={`slug-edit-${restaurant.id}`}>
        <label className="block text-[10px] font-bold text-emerald-800 uppercase tracking-wide mb-1">
          Xüsusi URL
        </label>
        <div className="flex items-center gap-1 text-xs">
          <span className="text-stone-500 shrink-0">{displayHost}/</span>
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="baton-cafe"
            className="flex-1 min-w-0 px-2 py-1 border border-emerald-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
            data-testid={`slug-input-${restaurant.id}`}
          />
        </div>
        {error && <p className="mt-1 text-[11px] text-red-600" data-testid={`slug-error-${restaurant.id}`}>{error}</p>}
        <p className="mt-1 text-[10px] text-emerald-700">Yalnız kiçik hərflər, rəqəmlər və &quot;-&quot; işarəsi.</p>
        <div className="mt-2 flex gap-1.5">
          <button
            onClick={save}
            disabled={saving}
            className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] rounded font-semibold disabled:opacity-50"
            data-testid={`slug-save-${restaurant.id}`}
          >
            {saving ? 'Yadda saxlanır…' : 'Yadda saxla'}
          </button>
          <button
            onClick={() => { setEditing(false); setValue(restaurant.slug || ''); setError(''); }}
            disabled={saving}
            className="px-3 py-1 bg-stone-200 hover:bg-stone-300 text-stone-700 text-[11px] rounded font-semibold"
            data-testid={`slug-cancel-${restaurant.id}`}
          >
            Ləğv et
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2 px-2.5 py-2 bg-stone-50 border border-stone-200 rounded-lg" data-testid={`slug-display-${restaurant.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold text-stone-500 uppercase tracking-wide">Xüsusi URL</p>
          <p className="text-xs font-mono text-stone-800 truncate" title={fullUrl}>
            <span className="text-stone-400">{displayHost}/</span>
            <span className="font-semibold text-emerald-700">{restaurant.slug}</span>
          </p>
        </div>
        <div className="flex gap-1 shrink-0">
          <button
            onClick={copyUrl}
            className="px-2 py-1 bg-white hover:bg-stone-100 border border-stone-300 rounded text-[11px] font-semibold text-stone-700"
            title="Kopyala"
            data-testid={`slug-copy-${restaurant.id}`}
          >
            📋
          </button>
          <button
            onClick={() => setEditing(true)}
            className="px-2 py-1 bg-white hover:bg-stone-100 border border-stone-300 rounded text-[11px] font-semibold text-stone-700"
            title="Redaktə et"
            data-testid={`slug-edit-btn-${restaurant.id}`}
          >
            ✏️
          </button>
        </div>
      </div>
    </div>
  );
};
